import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Truck, BadgePercent } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const inputCls = "tq-input !py-2 !text-xs";

function RuleModal({ title, fields, initial, onClose, onSaved, testid }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onSaved(f);
      onClose();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid={`${testid}-modal`}>
      <form onSubmit={save} className="bg-white w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-xl">{title}</p>
          <button type="button" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          {fields.map((fd) => (
            <div key={fd.key}>
              <label className="tq-label !text-[10px]">{fd.label}{fd.required ? " *" : ""}</label>
              {fd.type === "select" ? (
                <select value={f[fd.key]} onChange={(e) => setF({ ...f, [fd.key]: e.target.value })} data-testid={`${testid}-${fd.key}`} className={`${inputCls} bg-white`}>
                  {fd.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : (
                <input type={fd.type || "text"} required={fd.required} value={f[fd.key] ?? ""} placeholder={fd.placeholder}
                  onChange={(e) => setF({ ...f, [fd.key]: fd.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                  data-testid={`${testid}-${fd.key}`} className={inputCls} />
              )}
              {fd.hint && <p className="text-[10px] text-[#9a938a] mt-1">{fd.hint}</p>}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-5">
          <button disabled={busy} data-testid={`${testid}-save`} className="tq-btn-dark flex-1 !py-2.5">{busy ? "Saving…" : "Save Rule"}</button>
          <button type="button" onClick={onClose} className="tq-btn-outline !py-2.5">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function RuleTable({ icon: Icon, title, subtitle, rules, onAdd, onEdit, onToggle, onDelete, columns, testid }) {
  return (
    <div className="bg-white border border-[#E5DEC9] p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <p className="font-serif text-xl flex items-center gap-2"><Icon size={18} className="text-[#C5A059]" /> {title}</p>
        <button onClick={onAdd} data-testid={`${testid}-add`} className="tq-btn-dark !py-2"><Plus size={13} /> Add Rule</button>
      </div>
      <p className="text-xs text-[#8C7A5E] mb-4">{subtitle}</p>
      {!rules ? <div className="h-20 bg-[#F4EFE6] animate-pulse" /> : rules.length === 0 ? (
        <p className="text-sm text-[#9a938a] py-4 text-center" data-testid={`${testid}-empty`}>No rules yet — add one to get started.</p>
      ) : (
        <div className="divide-y divide-[#F4EFE6] border-t border-[#F4EFE6]">
          {rules.map((r) => (
            <div key={r.id} className="py-3 flex items-center gap-3 text-sm" data-testid={`${testid}-row-${r.id}`}>
              <button type="button" role="switch" aria-checked={r.enabled} onClick={() => onToggle(r)} data-testid={`${testid}-toggle-${r.id}`}
                className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ${r.enabled ? "bg-[#1A3C34]" : "bg-[#E5DEC9]"}`} aria-label="Toggle rule">
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${r.enabled ? "translate-x-[1.1rem]" : "translate-x-0.5"}`} />
              </button>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${r.enabled ? "" : "text-[#9a938a] line-through"}`}>{r.name}</p>
                <p className="text-[11px] text-[#9a938a] truncate">{columns(r)}</p>
              </div>
              <button onClick={() => onEdit(r)} aria-label="Edit rule" data-testid={`${testid}-edit-${r.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#D4AF37]"><Pencil size={13} /></button>
              <button onClick={() => onDelete(r)} aria-label="Delete rule" data-testid={`${testid}-delete-${r.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#8B1E21] hover:text-[#8B1E21]"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SHIP_FIELDS = [
  { key: "name", label: "Rule Name", required: true, placeholder: "e.g. Light orders under ₹999" },
  { key: "min_weight_g", label: "Min Weight (g)", type: "number", hint: "Leave blank for no lower limit" },
  { key: "max_weight_g", label: "Max Weight (g)", type: "number", hint: "Leave blank for no upper limit" },
  { key: "min_cart", label: "Min Order MRP (₹)", type: "number", hint: "Cart MRP total slab — blank = no minimum" },
  { key: "max_cart", label: "Max Order MRP (₹)", type: "number", hint: "Blank = no maximum" },
  { key: "fee", label: "Shipping Fee (₹)", type: "number", required: true, hint: "0 = free shipping for this slab" },
  { key: "order", label: "Priority (lower = checked first)", type: "number" },
];

const PREPAID_FIELDS = [
  { key: "name", label: "Offer Name", required: true, placeholder: "e.g. Prepaid Saver" },
  { key: "min_order", label: "Minimum Order Value (₹)", type: "number", hint: "0 = applies to all prepaid orders" },
  { key: "discount_type", label: "Extra Discount Type", type: "select", options: [["percent", "Percentage (%)"], ["flat", "Flat Amount (₹)"]] },
  { key: "discount_value", label: "Extra Discount Value", type: "number", hint: "% or ₹ based on type above" },
  { key: "shipping_discount_type", label: "Shipping Benefit", type: "select", options: [["none", "None"], ["free", "Free Shipping"], ["percent", "Shipping % Off"], ["flat", "Shipping ₹ Off"]] },
  { key: "shipping_discount_value", label: "Shipping Benefit Value", type: "number", hint: "Ignored for None / Free" },
];

export function ShippingRules() {
  const [rules, setRules] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/shipping-rules").then(({ data }) => setRules(data)).catch((e) => toast.error(e.friendlyMessage));
  useEffect(() => { load(); }, []);
  const blank = { name: "", enabled: true, min_weight_g: "", max_weight_g: "", min_cart: "", max_cart: "", fee: 0, order: 0 };
  return (
    <>
      <RuleTable icon={Truck} title="Shipping Rate Rules" testid="ship-rules" rules={rules}
        subtitle="Weight slabs + order MRP slabs. First matching rule (by priority) applies; unmatched orders fall back to the default fee / free-above settings above. Changes go live instantly."
        columns={(r) => [
          r.min_weight_g !== null && r.min_weight_g !== "" ? `≥${r.min_weight_g}g` : null,
          r.max_weight_g !== null && r.max_weight_g !== "" ? `≤${r.max_weight_g}g` : null,
          r.min_cart !== null && r.min_cart !== "" ? `MRP ≥ ₹${r.min_cart}` : null,
          r.max_cart !== null && r.max_cart !== "" ? `MRP ≤ ₹${r.max_cart}` : null,
        ].filter(Boolean).join(" · ") || "All orders" + ` → ₹${r.fee}` + ` · priority ${r.order}`}
        onAdd={() => setEditing({ ...blank })}
        onEdit={(r) => setEditing({ ...r })}
        onToggle={async (r) => { await api.put(`/admin/shipping-rules/${r.id}`, { enabled: !r.enabled }); load(); }}
        onDelete={async (r) => { if (!window.confirm(`Delete rule "${r.name}"?`)) return; await api.delete(`/admin/shipping-rules/${r.id}`); toast.success("Rule deleted"); load(); }} />
      {editing && (
        <RuleModal title={editing.id ? "Edit Shipping Rule" : "New Shipping Rule"} fields={SHIP_FIELDS} initial={editing} testid="ship-rule"
          onClose={() => setEditing(null)}
          onSaved={async (f) => {
            const payload = { ...f, min_weight_g: f.min_weight_g === "" ? null : f.min_weight_g, max_weight_g: f.max_weight_g === "" ? null : f.max_weight_g, min_cart: f.min_cart === "" ? null : f.min_cart, max_cart: f.max_cart === "" ? null : f.max_cart };
            if (editing.id) await api.put(`/admin/shipping-rules/${editing.id}`, payload);
            else await api.post("/admin/shipping-rules", payload);
            toast.success("Shipping rule saved — live on storefront");
            load();
          }} />
      )}
    </>
  );
}

export function PrepaidRules() {
  const [rules, setRules] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/prepaid-rules").then(({ data }) => setRules(data)).catch((e) => toast.error(e.friendlyMessage));
  useEffect(() => { load(); }, []);
  const blank = { name: "", enabled: true, min_order: 0, discount_type: "percent", discount_value: 0, shipping_discount_type: "none", shipping_discount_value: 0 };
  return (
    <>
      <RuleTable icon={BadgePercent} title="Prepaid / Online Payment Offers" testid="prepaid-rules" rules={rules}
        subtitle="Rewards for customers paying online (UPI/cards via Razorpay). When several rules match, the one with the highest minimum order value wins. Changes go live instantly."
        columns={(r) => `${r.min_order ? `Orders ≥ ₹${r.min_order}` : "All orders"} → ${r.discount_value > 0 ? (r.discount_type === "percent" ? `${r.discount_value}% off` : `₹${r.discount_value} off`) : "no extra discount"}${r.shipping_discount_type !== "none" ? ` + ${r.shipping_discount_type === "free" ? "FREE shipping" : r.shipping_discount_type === "percent" ? `${r.shipping_discount_value}% off shipping` : `₹${r.shipping_discount_value} off shipping`}` : ""}`}
        onAdd={() => setEditing({ ...blank })}
        onEdit={(r) => setEditing({ ...r })}
        onToggle={async (r) => { await api.put(`/admin/prepaid-rules/${r.id}`, { enabled: !r.enabled }); load(); }}
        onDelete={async (r) => { if (!window.confirm(`Delete offer "${r.name}"?`)) return; await api.delete(`/admin/prepaid-rules/${r.id}`); toast.success("Offer deleted"); load(); }} />
      {editing && (
        <RuleModal title={editing.id ? "Edit Prepaid Offer" : "New Prepaid Offer"} fields={PREPAID_FIELDS} initial={editing} testid="prepaid-rule"
          onClose={() => setEditing(null)}
          onSaved={async (f) => {
            if (editing.id) await api.put(`/admin/prepaid-rules/${editing.id}`, f);
            else await api.post("/admin/prepaid-rules", f);
            toast.success("Prepaid offer saved — live on storefront");
            load();
          }} />
      )}
    </>
  );
}
